// @fileoverview This is a composable for the photo album editor.
// It is used to handle the photo album editor's state and actions.
// It is also used to handle the photo album editor's content picker sources and search modes.

import { getAttachmentsFromDraft } from '@@/bits/post_drafts'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import type { Cid, PostAttributes } from '@@/types'
import useLogger from '@@/vuecomposables/logger'
import { usePostAttachmentUploader } from '@@/vuecomposables/usePostAttachmentUploader'
import { createSharedComposable } from '@vueuse/core'
import { ref } from 'vue'

const PLACEHOLDER_UPLOADING_ATTACHMENT = { url: '', progress: 0, isUploadingFile: true }
const logger = useLogger('photo-album-editor', { logLifecycleHooks: false, log: true })

export const usePhotoAlbumEditor = createSharedComposable(() => {
  const contentPickerStore = useContentPickerStore()
  const surfaceContentPickerStore = useSurfaceContentPickerStore()
  const currentItemIndex = ref(0)

  const updatePhotoAlbumAttachmentIndex = (index: number): void => {
    currentItemIndex.value = index
  }

  // #region Photo album image picker and photo editor dialog visibility
  const surfacePanelsStore = useSurfacePanelsStore()
  const toShowEditorDialogAfterClosingImagePicker = ref(false)
  const showPhotoAlbumImagePicker = (
    { toShowEditorDialog }: { toShowEditorDialog: boolean } = { toShowEditorDialog: false },
  ): void => {
    surfaceContentPickerStore.hidePostContentPicker()
    surfacePanelsStore.xPhotoAlbumImagePicker = true
    contentPickerStore.showContentSourceMenu({ contentPickerContext: 'photo-album-image-picker' })
    toShowEditorDialogAfterClosingImagePicker.value = toShowEditorDialog
  }

  const hidePhotoAlbumImagePicker = (): void => {
    surfacePanelsStore.xPhotoAlbumImagePicker = false
    contentPickerStore.hideContentSourceMenu()
    if (toShowEditorDialogAfterClosingImagePicker.value) {
      showPhotoAlbumEditorDialog()
      toShowEditorDialogAfterClosingImagePicker.value = false
    }
  }

  const showPhotoAlbumEditorDialog = (): void => {
    surfacePanelsStore.xPhotoAlbumEditorDialog = true
  }

  const hidePhotoAlbumEditorDialog = (): void => {
    surfacePanelsStore.xPhotoAlbumEditorDialog = false
  }
  // #endregion

  // #region Image upload logic
  const surfaceStore = useSurfaceStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const { startUploadFile, stopUploadFile } = usePostAttachmentUploader()

  const getDraftAndCid = (postCid?: Cid): { draft: PostAttributes | null; cid?: Cid } => {
    if (postCid == null) {
      return { draft: surfaceDraftsStore.activeDraft, cid: surfaceDraftsStore.activeDraft?.cid }
    }
    return { draft: surfaceDraftsStore.getDraftFromCid(postCid), cid: postCid }
  }

  /**
   * Sets placeholder attachments in a draft up to the specified index
   * Used to show upload progress states while files are being uploaded
   * @param draft - The post draft to add placeholder attachments to
   * @param index - The target index up to which placeholders should be added
   */
  const setPlaceholderAttachments = (draft: PostAttributes, index?: number): void => {
    const existingAttachments = getAttachmentsFromDraft(draft)
    const targetIndex = Math.max(index ?? existingAttachments.length, 1)
    const attachments = [...existingAttachments]
    while (attachments.length <= targetIndex) {
      attachments.push(PLACEHOLDER_UPLOADING_ATTACHMENT)
    }

    surfaceDraftsStore.updateDraft({
      attachments,
      cid: draft.cid,
    })
  }

  const onUploadStart = ({
    file,
    url, // for Google Drive. unnecessary for now.
    postCid,
    index,
  }: {
    file?: File
    url?: string
    postCid?: Cid
    index?: number
  }): void => {
    logger.log('onUploadStart', { file, url, postCid, index })

    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null || file == null) return

    const attachments = getAttachmentsFromDraft(draft)
    setPlaceholderAttachments(draft, index ?? attachments.length)

    // TODO: use custom max file upload size if given
    const maxFileUploadSize = surfaceStore.uploadLimit
    startUploadFile({ cid, file, attachmentIndex: index ?? attachments.length, maxFileUploadSize })
  }

  const onUploadProgress = ({
    progress,
    postCid,
    index,
  }: {
    progress: number
    postCid?: Cid
    index?: number
  }): void => {
    logger.log('onUploadProgress', { progress, postCid, index })
    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)
    attachments[index ?? attachments.length].progress = progress
    surfaceDraftsStore.updateDraft({ attachments, cid })
  }

  const onUploadCancel = (postCid?: Cid, index?: number): void => {
    logger.log('onUploadCancel', { postCid, index })
    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)
    stopUploadFile({ cid, attachmentIndex: index ?? attachments.length })
    const updatedAttachments = [...attachments]
    updatedAttachments.splice(index ?? attachments.length, 1)
    surfaceDraftsStore.updateDraft({
      attachments: updatedAttachments,
      cid,
    })
  }

  const onUploadDone = (url: string, payload?: { altText?: string; postCid?: Cid }): void => {
    logger.log('onUploadDone', { url, payload })
    const { draft, cid } = getDraftAndCid(payload?.postCid)
    if (draft == null || cid == null) return
    const attachments = [...getAttachmentsFromDraft(draft), { url, alt: payload?.altText }]

    // discard progress states of attachments
    surfaceDraftsStore.updateDraft({
      attachments: attachments.map((a) => ({ url: a.url, alt: a.alt, signed_url: a.signed_url, hotlink: a.hotlink })),
      cid,
    })
    currentItemIndex.value = attachments.length - 1
  }

  const onUploadError = (errorSource?: File | any): void => {}

  const selectFile = ({ file }: { file: File }): void => {
    onUploadStart({ file })
  }

  const selectLink = (url: string, payload: { altText?: string }): void => {
    logger.log('selectLink', { url })
    onUploadDone(url, payload)
  }

  const selectFiles = ({ files }: { files: File[] }): void => {
    const { draft, cid } = getDraftAndCid()
    if (draft == null || cid == null) return

    const attachments = getAttachmentsFromDraft(draft)
    files.forEach((file, index) => {
      onUploadStart({ file, index: index + attachments.length, postCid: cid })
    })
  }
  // #endregion

  return {
    showPhotoAlbumImagePicker,
    hidePhotoAlbumImagePicker,
    showPhotoAlbumEditorDialog,
    hidePhotoAlbumEditorDialog,

    onUploadStart,
    onUploadProgress,
    onUploadDone,
    onUploadCancel,
    onUploadError,
    selectFile,
    selectFiles,
    selectLink,

    currentItemIndex,
    updatePhotoAlbumAttachmentIndex,
  }
})
